import React, { useRef, useCallback, useEffect, useState, useMemo } from "react";
import { Container, Sprite, Graphics } from "@pixi/react";
import { getNewUITexture } from "../../../../assets";

/**
 * PopiVolumeBar - Interactive volume slider component
 *
 * Features:
 * - Draggable handle for smooth volume adjustment
 * - Responsive sizing based on buttonConfig
 *
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} value - Current volume value (0-1)
 * @param {function} onChange - Callback when volume changes
 * @param {object} buttonConfig - Responsive button configuration from useDimensions
 */
const VolumeBar = ({
  x = 0,
  y = 0,
  value = 0.5,
  onChange,
  buttonConfig,
  maxWidth,
  heightScale = 1.0,
}) => {
  const containerRef = useRef(null);
  const handleRef = useRef(null);
  const backgroundRef = useRef(null);
  const foregroundRef = useRef(null);
  const maskRef = useRef(null);

  // State for dragging
  const [isDragging, setIsDragging] = useState(false);
  const dragStartXRef = useRef(0);
  const initialValueRef = useRef(0);

  // Get textures
  const backgroundTexture = getNewUITexture("volume_bar_background");
  const foregroundTexture = getNewUITexture("volume_bar_foreground");
  const buttonTexture = getNewUITexture("volume_bar_handle");

  // Calculate dimensions based on background texture and buttonConfig
  const getScaledDimensions = useCallback(() => {
    if (!backgroundTexture || !buttonConfig) {
      return { scale: 1, trackWidth: 300, height: 50 };
    }

    const textureWidth = backgroundTexture.width;
    const textureHeight = backgroundTexture.height;

    // Use maxWidth if provided, otherwise fall back to buttonConfig-based sizing
    const desiredWidth = maxWidth || buttonConfig.width * 5.5;
    const scale = desiredWidth / textureWidth;

    return {
      scale,
      trackWidth: desiredWidth,
      height: textureHeight * scale * heightScale,
    };
  }, [backgroundTexture, buttonConfig, maxWidth, heightScale]);

  // Memoize dimensions calculation
  const dimensions = useMemo(() => getScaledDimensions(), [getScaledDimensions]);

  // Calculate handle position based on value (0-1) - memoized
  const handleX = useMemo(
    () => {
      // Handle moves across full track width (0 to trackWidth)
      return dimensions.trackWidth * value;
    },
    [dimensions.trackWidth, value]
  );

  // Memoize cursor value
  const cursor = useMemo(
    () => isDragging ? "grabbing" : "grab",
    [isDragging]
  );

  // Memoize anchor objects to prevent recreation on every render
  const backgroundAnchor = useMemo(() => ({ x: 0, y: 0.5 }), []);
  const foregroundAnchor = useMemo(() => ({ x: 0, y: 0.5 }), []);

  // Memoize draw function for mask Graphics component
  const drawMask = useCallback((g) => {
    g.clear();
    g.beginFill(0xffffff);
    g.drawRect(0, -dimensions.height / 2, handleX, dimensions.height);
    g.endFill();
  }, [dimensions.height, handleX]);

  // Handle pointer down on handle (start dragging)
  const handlePointerDown = useCallback(
    (event) => {
      event.stopPropagation();
      setIsDragging(true);

      // Use global position from PixiJS event
      const startX = event.global?.x || event.data?.global?.x;
      dragStartXRef.current = startX;
      initialValueRef.current = value;
    },
    [value]
  );

  // Click/tap on track → jump handle to that position (then allow drag)
  const handleTrackPointerDown = useCallback(
    (event) => {
      event.stopPropagation();
      if (!containerRef.current) return;

      const global = event.global || event.data?.global;
      if (!global) return;

      const local = containerRef.current.toLocal(global);
      const newValue = Math.max(
        0,
        Math.min(1, local.x / dimensions.trackWidth)
      );


      if (onChange) {
        onChange(newValue);
      }

      // Continue as a drag from the clicked position
      setIsDragging(true);
      dragStartXRef.current = global.x;
      initialValueRef.current = newValue;
    },
    [onChange, dimensions.trackWidth]
  );

  // Handle pointer move (dragging)
  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (event) => {
      // Use clientX for native DOM events
      const currentX = event.clientX || event.touches?.[0]?.clientX;
      if (currentX === undefined) return;

      const deltaX = currentX - dragStartXRef.current;
      const deltaValue = deltaX / dimensions.trackWidth;
      const newValue = Math.max(
        0,
        Math.min(1, initialValueRef.current + deltaValue)
      );

      if (onChange) {
        onChange(newValue);
      }
    };

    const handlePointerUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    window.addEventListener("touchmove", handlePointerMove);
    window.addEventListener("touchend", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("touchend", handlePointerUp);
    };
  }, [isDragging, onChange, dimensions]);

  // Don't render if textures not loaded
  if (!backgroundTexture || !foregroundTexture || !buttonTexture) {
    return null;
  }

  return (
    <Container ref={containerRef} name="secondary_volume_bar" x={x} y={y}>
      {/* Background track — clickable to jump volume to that position */}
      <Sprite
        ref={backgroundRef}
        texture={backgroundTexture}
        name="volume_bar_background"
        anchor={backgroundAnchor}
        width={dimensions.trackWidth}
        height={dimensions.height}
        roundPixels={true}
        eventMode="static"
        cursor="pointer"
        onpointerdown={handleTrackPointerDown}
      />

      {/* Foreground (filled portion) - masked to show only up to handle position */}
      <Container name="foreground_container">
        <Graphics
          ref={maskRef}
          draw={drawMask}
        />
        <Sprite
          ref={foregroundRef}
          texture={foregroundTexture}
          name="volume_bar_foreground"
          anchor={foregroundAnchor}
          width={dimensions.trackWidth}
          height={dimensions.height}
          roundPixels={true}
          mask={maskRef.current}
          eventMode="static"
          cursor="pointer"
          onpointerdown={handleTrackPointerDown}
        />
      </Container>

      {/* Draggable handle */}
      <Sprite
        ref={handleRef}
        texture={buttonTexture}
        name="volume_bar_handle"
        anchor={0.5}
        x={handleX}
        y={0}
        width={dimensions.height * 1.2}
        height={dimensions.height * 1.2}
        roundPixels={true}
        eventMode="static"
        cursor={cursor}
        onpointerdown={handlePointerDown}
      />
    </Container>
  );
};

export default VolumeBar;
